import React, { useState, useEffect } from 'react';
import './App.css';
import Logo from './components/Logo';
import SlidingBanner from './components/Banner/SlidingBanner';
import LocationSelector from './components/LocationSelector/LocationSelector';
import ItemUpload from './components/ItemList/ItemUpload';
import CustomerChatModal from './components/CustomerChatModal';
import AuthScreen from './components/AuthScreen';
import OrderTrackingMap from './components/OrderTrackingMap';
import LocationPermission from './components/LocationPermission';
import PaymentScreen from './components/PaymentScreen';
import CategoryTabs from './components/CategoryTabs';
import StoreList from './components/StoreList';
import StorePage from './components/StorePage';
import CheckoutCart from './components/CheckoutCart';
import { userAPI } from './services/api';

const DEFAULT_BANNERS = [
  { id: '1', title: 'WELCOME TO THUNDERBAG - We deliver Grocery, Vegetables, Meat, Food, Pharmacy & more at just ₹50', subtitle: 'థండర్‌బ్యాగ్‌కు స్వాగతం - కిరాణా, కూరగాయలు, మాంసం, ఆహారం, మందులు మరిన్ని కేవలం ₹50 తో', color: '#FF6B6B', icon: '🛒🥕🍖🍔💊' },
  { id: '2', title: 'Fresh Vegetables & Fruits - Delivered to Your Door', subtitle: 'తాజా కూరగాయలు & పండ్లు - మీ ఇంటికి డెలివరీ', color: '#4ECDC4', icon: '🥬🍅🥒🥕🍎' },
  { id: '3', title: 'Pharmacy & Healthcare - Quick Delivery', subtitle: 'మందుల దుకాణం & ఆరోగ్య సంరక్షణ - త్వరిత డెలివరీ', color: '#95E1D3', icon: '💊💉🩺🏥' },
];

function App() {
  const [locationGranted, setLocationGranted] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [items, setItems] = useState<string[]>([]);
  const [showProfile, setShowProfile] = useState(false);
  const [showOrderStatus, setShowOrderStatus] = useState(false);
  const [userName, setUserName] = useState('John Doe');
  const [savedAddress, setSavedAddress] = useState('123 Main Street, City');
  const [orders, setOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [paymentPopup, setPaymentPopup] = useState<any>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('UPI');
  const [partnerArrivedPopup, setPartnerArrivedPopup] = useState<any>(null);
  const [otpInput, setOtpInput] = useState('');
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [deliveryTip, setDeliveryTip] = useState(0);
  const [customDeliveryTip, setCustomDeliveryTip] = useState('');
  const [chatModal, setChatModal] = useState<{orderId: string, partnerName: string, orderStatus: string} | null>(null);
  const [trackingOrder, setTrackingOrder] = useState<any>(null);
  
  // New store-based shopping states
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStore, setSelectedStore] = useState<any>(null);
  const [cart, setCart] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<'categories' | 'list'>('list'); // 'list' for old ItemUpload, 'categories' for new store system
  const [showCheckout, setShowCheckout] = useState(false);
  
  // Direct Custom Order (Order Anything)
  const [showDirectCustomOrder, setShowDirectCustomOrder] = useState(false);
  const [directOrderDescription, setDirectOrderDescription] = useState('');
  const [directOrderItems, setDirectOrderItems] = useState<{name: string, quantity: string}[]>([{ name: '', quantity: '' }]);
  const [directOrderSubmitting, setDirectOrderSubmitting] = useState(false);
  const [directOrderPaymentPopup, setDirectOrderPaymentPopup] = useState<any>(null);
  const [storePaymentPopup, setStorePaymentPopup] = useState<any>(null);
  const [paymentReceiptImage, setPaymentReceiptImage] = useState('');
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [banners, setBanners] = useState<any[]>(DEFAULT_BANNERS);

  // Fetch banners from API
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await userAPI.getBanners();
        if (response.data && response.data.length > 0) {
          // Transform API banners to match SlidingBanner format
          const transformedBanners = response.data.map((b: any) => ({
            id: b._id,
            title: b.title,
            subtitle: b.subtitle,
            color: `linear-gradient(135deg, ${b.backgroundColor} 0%, ${b.gradientColor} 100%)`,
            icon: b.icon,
            imageUrl: b.imageUrl,
            textColor: b.textColor,
            titleFontSize: b.titleFontSize,
            subtitleFontSize: b.subtitleFontSize,
            fontFamily: b.fontFamily
          }));
          setBanners(transformedBanners);
        }
      } catch (error) {
        console.log('Using default banners');
      }
    };
    fetchBanners();
  }, []);

  const handleLocationGranted = (location: { lat: number; lng: number }) => {
    setUserLocation(location);
    setLocationGranted(true);
  };

  const fetchOrders = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setOrders([]);
      return;
    }

    setLoadingOrders(true);
    try {
      const response = await userAPI.getMyOrders();
      const fetchedOrders = response.data;
      setOrders(fetchedOrders);
      
      // Check for payment pending orders
      const paymentPendingOrder = fetchedOrders.find((o: any) => 
        o.status === 'Payment Pending' && o.itemsTotal > 0
      );
      
      if (paymentPendingOrder && (!paymentPopup || paymentPopup._id !== paymentPendingOrder._id)) {
        setPaymentPopup(paymentPendingOrder);
        
        // Show browser notification
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('💰 Payment Required!', {
            body: `Partner waiting at store. Bill: ₹${paymentPendingOrder.itemsTotal}. Tap to pay now.`,
            icon: '/logo192.png',
            badge: '/logo192.png',
            tag: 'payment-pending',
            requireInteraction: true
          });
        }
        
        // Play notification sound
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZUQ0PVK3n77BdGAg+mtv0xnYnBSyBzvLaiTcIGWi77eefTRAMUKfj8LZjHAY4ktfyzHksBSR3x/DdkEAKFF606+uoVRQKRp/g8r5sIQUxh9Hz04IzBh5uwO/jmVENfQoAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA');
        audio.play().catch(() => {});
      }

      // Check for partner arrived at customer location
      const partnerArrivedOrder = fetchedOrders.find((o: any) => 
        o.status === 'At Customer Location' && o.deliveryConfirmation?.otp
      );
      
      if (partnerArrivedOrder && (!partnerArrivedPopup || partnerArrivedPopup._id !== partnerArrivedOrder._id)) {
        setPartnerArrivedPopup(partnerArrivedOrder);
      }

      // Check for direct custom order with partner assigned (needs delivery charge payment)
      const directOrderWithPartner = fetchedOrders.find((o: any) => 
        o.isDirectCustomOrder && 
        o.status === 'Partner Assigned' && 
        o.assignedPartner
      );
      
      if (directOrderWithPartner && (!directOrderPaymentPopup || directOrderPaymentPopup._id !== directOrderWithPartner._id)) {
        setDirectOrderPaymentPopup(directOrderWithPartner);
        
        // Show browser notification
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('🎉 Partner Accepted Your Order!', {
            body: `Pay ₹${directOrderWithPartner.deliveryCharges || 60} delivery + item costs to partner. Tap to pay now.`,
            icon: '/logo192.png',
            tag: 'direct-order-payment',
            requireInteraction: true
          });
        }
      }

      // Check for direct custom order waiting for store payment (partner sent QR code)
      const storePaymentOrder = fetchedOrders.find((o: any) => 
        o.isDirectCustomOrder && 
        o.status === 'Waiting for Payment' && 
        o.directPaymentRequest?.qrCodeImage
      );
      
      if (storePaymentOrder && (!storePaymentPopup || storePaymentPopup._id !== storePaymentOrder._id)) {
        setStorePaymentPopup(storePaymentOrder);
        
        // Show browser notification
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('💳 Pay at Store!', {
            body: `Partner is at ${storePaymentOrder.directPaymentRequest?.storeName || 'store'}. Pay ₹${storePaymentOrder.directPaymentRequest?.amount} now.`,
            icon: '/logo192.png',
            tag: 'store-payment',
            requireInteraction: true
          });
        }
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
      // You can also fetch user details here if needed
    }
  }, []);

  // Poll for order updates every 10 seconds
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchOrders();
      const interval = setInterval(fetchOrders, 10000);
      
      // Request notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
      
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const handleLogin = (token: string, user: any) => {
    setIsAuthenticated(true);
    setCurrentUser(user);
    setUserName(user.name || 'User');
    if (user.address) {
      setSavedAddress(user.address);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setCurrentUser(null);
    setOrders([]);
  };

  // Check if app was freshly launched (not from recent apps)
  useEffect(() => {
    const appWasRunning = sessionStorage.getItem('appWasRunning');
    
    if (!appWasRunning) {
      // App was freshly launched (removed from recent apps)
      setShowWelcomePopup(true);
      sessionStorage.setItem('appWasRunning', 'true');
    }

    // Mark app as running in sessionStorage
    // This persists only during the session, cleared when app is closed
    const markAppRunning = () => {
      sessionStorage.setItem('appWasRunning', 'true');
    };

    window.addEventListener('load', markAppRunning);
    
    return () => {
      window.removeEventListener('load', markAppRunning);
    };
  }, []);

  const handleOrderStatus = () => {
    setShowOrderStatus(true);
    fetchOrders();
  };

  const handleTrackOrder = (orderId: string) => {
    const order = orders.find(o => o._id === orderId);
    if (order) {
      setTrackingOrder(order);
    }
  };

  const handlePaymentComplete = async () => {
    if (!paymentPopup) return;
    
    try {
      const orderId = paymentPopup._id;
      await userAPI.confirmPayment(orderId, 'UPI');
      alert('✅ Payment Confirmed!\n\nYour order will be delivered soon.');
      setPaymentPopup(null);
      fetchOrders();
    } catch (error: any) {
      alert('Failed to confirm payment: ' + (error.response?.data?.message || 'Unknown error'));
    }
  };

  const handleConfirmDelivery = async () => {
    if (!partnerArrivedPopup || !otpInput) {
      alert('Please enter the OTP');
      return;
    }
    
    try {
      await userAPI.confirmDelivery(partnerArrivedPopup._id, otpInput, deliveryTip);
      const tipMsg = deliveryTip > 0 ? ` Thank you for adding ₹${deliveryTip} tip!` : '';
      alert(`✅ Delivery confirmed!${tipMsg} Thank you for your order!`);
      setPartnerArrivedPopup(null);
      setOtpInput('');
      setDeliveryTip(0);
      setCustomDeliveryTip('');
      fetchOrders();
    } catch (error: any) {
      alert('Failed to confirm delivery: ' + (error.response?.data?.message || 'Invalid OTP'));
    }
  };

  const handleDeliveryTipSelect = (amount: number) => {
    setDeliveryTip(amount);
    setCustomDeliveryTip('');
  };

  const handleCustomDeliveryTip = () => {
    const amount = parseInt(customDeliveryTip);
    if (!isNaN(amount) && amount >= 0) {
      setDeliveryTip(amount);
    } else {
      alert('Please enter a valid tip amount');
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    if (window.confirm('Are you sure you want to cancel this order?')) {
      try {
        await userAPI.cancelOrder(orderId);
        alert('Order cancelled successfully');
        fetchOrders(); // Refresh orders list
      } catch (error: any) {
        alert('Failed to cancel order: ' + (error.response?.data?.message || 'Unknown error'));
      }
    }
  };

  const handleConfirmCustomBill = async (orderId: string, confirmed: boolean) => {
    if (!confirmed) {
      if (!window.confirm('Are you sure you want to reject this bill? The order will be cancelled.')) {
        return;
      }
    }
    
    try {
      await userAPI.confirmCustomBill(orderId, confirmed, 'Cash on Delivery');
      if (confirmed) {
        alert('✅ Bill confirmed! Your order will be prepared and delivered.');
      } else {
        alert('Order cancelled.');
      }
      fetchOrders();
    } catch (error: any) {
      alert('Failed: ' + (error.response?.data?.message || 'Unknown error'));
    }
  };

  const handleYourOrders = () => {
    setShowOrderStatus(true);
    fetchOrders();
  };

  const handleYourProfile = () => {
    setShowProfile(true);
  };

  const handleChangeName = () => {
    const newName = prompt('Enter your name:', userName);
    if (newName && newName.trim()) {
      setUserName(newName.trim());
      alert('Name updated successfully!');
    }
  };

  const handleChangeAddress = () => {
    const newAddress = prompt('Enter your address:', savedAddress);
    if (newAddress && newAddress.trim()) {
      setSavedAddress(newAddress.trim());
      alert('Address updated successfully!');
    }
  };

  // New store-based shopping handlers
  const handleAddToCart = (item: any, storeId: string, storeName: string) => {
    setCart(prevCart => {
      // For variant items, match by both _id and variantIndex
      // For custom quantity, treat as separate item
      const existingIndex = prevCart.findIndex(ci => {
        if (item.customQuantity) {
          return false; // Custom quantities are always new items
        }
        if (item.variantIndex !== undefined) {
          return ci._id === item._id && ci.variantIndex === item.variantIndex;
        }
        return ci._id === item._id && ci.variantIndex === undefined;
      });
      
      if (existingIndex >= 0) {
        const newCart = [...prevCart];
        const newQuantity = newCart[existingIndex].quantity + item.quantity;
        
        if (newQuantity <= 0) {
          newCart.splice(existingIndex, 1);
        } else {
          newCart[existingIndex] = {
            ...newCart[existingIndex],
            quantity: newQuantity
          };
        }
        return newCart;
      } else {
        if (item.quantity > 0) {
          return [...prevCart, {
            ...item,
            storeId,
            storeName,
            quantity: item.quantity,
            variantIndex: item.variantIndex,
            selectedVariant: item.selectedVariant,
            customQuantity: item.customQuantity
          }];
        }
        return prevCart;
      }
    });
  };

  const handleSelectStore = (store: any) => {
    setSelectedStore(store);
  };

  const handleBackFromStore = () => {
    setSelectedStore(null);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      alert('Your cart is empty!');
      return;
    }
    setShowCheckout(true);
  };

  const handleOrderPlaced = () => {
    setCart([]);
    setShowCheckout(false);
    fetchOrders();
  };

  const handleRemoveFromCart = (itemId: string, variantIndex?: number) => {
    setCart(prevCart => {
      return prevCart.filter(item => {
        if (variantIndex !== undefined) {
          return !(item._id === itemId && item.variantIndex === variantIndex);
        }
        return item._id !== itemId || item.variantIndex !== undefined;
      });
    });
  };

  const handleUpdateCartQuantity = (itemId: string, newQuantity: number, variantIndex?: number) => {
    setCart(prevCart => {
      return prevCart.map(item => {
        const isMatch = variantIndex !== undefined
          ? (item._id === itemId && item.variantIndex === variantIndex)
          : (item._id === itemId && item.variantIndex === undefined);
        
        if (isMatch) {
          if (newQuantity <= 0) {
            return null;
          }
          return { ...item, quantity: newQuantity };
        }
        return item;
      }).filter(Boolean) as any[];
    });
  };

  // Show location permission screen first
  if (!locationGranted) {
    return <LocationPermission onLocationGranted={handleLocationGranted} />;
  }

  // Show auth screen if not authenticated
  if (!isAuthenticated) {
    return <AuthScreen onLogin={handleLogin} />;
  }

  return (
    <div className="app">
      <header className="app-header">
        <Logo size={35} showText={true} />
      </header>
      
      <main className="app-main">
        <SlidingBanner banners={banners} />
        
        {/* Order Anything - Custom Order Section */}
        <div 
          onClick={() => setShowDirectCustomOrder(true)}
          style={{
            background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 25%, #ff4757 50%, #ee5a24 75%, #ff6348 100%)',
            margin: '15px',
            padding: '25px',
            borderRadius: '24px',
            cursor: 'pointer',
            boxShadow: '0 10px 40px rgba(255, 107, 53, 0.5), 0 0 0 4px rgba(255,255,255,0.3), inset 0 0 30px rgba(255,255,255,0.15)',
            border: '3px solid rgba(255,255,255,0.6)',
            position: 'relative',
            overflow: 'hidden',
            animation: 'pulse 2s infinite'
          }}
        >
          {/* Animated background patterns */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 10% 20%, rgba(255,255,255,0.2) 0%, transparent 20%),
              radial-gradient(circle at 90% 80%, rgba(255,255,255,0.15) 0%, transparent 25%),
              radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)
            `,
            pointerEvents: 'none'
          }}></div>
          
          {/* Floating emojis */}
          <div style={{ position: 'absolute', top: '10px', left: '15px', fontSize: '30px', opacity: 0.3, transform: 'rotate(-15deg)' }}>🛒</div>
          <div style={{ position: 'absolute', top: '15px', right: '60px', fontSize: '25px', opacity: 0.25, transform: 'rotate(10deg)' }}>🎁</div>
          <div style={{ position: 'absolute', bottom: '15px', left: '50px', fontSize: '28px', opacity: 0.25, transform: 'rotate(-5deg)' }}>🛵</div>
          <div style={{ position: 'absolute', bottom: '10px', right: '20px', fontSize: '35px', opacity: 0.3, transform: 'rotate(15deg)' }}>📦</div>
          <div style={{ position: 'absolute', top: '50%', right: '15px', fontSize: '40px', opacity: 0.2 }}>✨</div>
          
          {/* Main Content - Full Width Centered */}
          <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
            {/* Icon Row */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '8px', 
              marginBottom: '12px',
              fontSize: '35px'
            }}>
              <span style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>🛍️</span>
              <span style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>📦</span>
              <span style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>🎯</span>
            </div>
            
            {/* Main Title */}
            <h3 style={{ 
              margin: '0 0 8px 0', 
              color: 'white', 
              fontSize: '26px', 
              fontWeight: '900',
              textShadow: '2px 2px 8px rgba(0,0,0,0.3), 0 0 20px rgba(255,255,255,0.3)',
              letterSpacing: '1px'
            }}>
              🚀 ORDER ANYTHING! 🚀
            </h3>
            
            {/* Telugu Title */}
            <p style={{ 
              margin: '0 0 15px 0', 
              color: '#fff9c4', 
              fontSize: '18px', 
              fontWeight: '700',
              textShadow: '1px 1px 4px rgba(0,0,0,0.3)'
            }}>
              ⭐ ఏదైనా ఆర్డర్ చేయండి! ⭐
            </p>
            
            {/* Feature Tags Row */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '8px',
              marginBottom: '15px'
            }}>
              <span style={{
                background: 'rgba(255,255,255,0.25)',
                backdropFilter: 'blur(5px)',
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '700',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)'
              }}>🏪 Any Shop</span>
              <span style={{
                background: 'rgba(255,255,255,0.25)',
                backdropFilter: 'blur(5px)',
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '700',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)'
              }}>🛵 Fast Delivery</span>
              <span style={{
                background: 'rgba(255,255,255,0.25)',
                backdropFilter: 'blur(5px)',
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '700',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)'
              }}>💰 Best Prices</span>
            </div>
            
            {/* Description */}
            <p style={{ 
              margin: '0 0 15px 0', 
              color: 'rgba(255,255,255,0.95)', 
              fontSize: '14px', 
              lineHeight: '1.6',
              textShadow: '1px 1px 3px rgba(0,0,0,0.2)'
            }}>
              🛵 Get anything delivered from any shop in your area!<br/>
              <span style={{ color: '#fff59d' }}>మీ ప్రాంతంలో ఏ షాప్ నుండైనా ఏదైనా డెలివరీ పొందండి</span>
            </p>
            
            {/* CTA Button */}
            <div style={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
              color: '#e74c3c',
              padding: '14px 35px',
              borderRadius: '30px',
              fontWeight: '800',
              fontSize: '16px',
              boxShadow: '0 6px 20px rgba(0,0,0,0.25), inset 0 2px 0 rgba(255,255,255,0.8)',
              border: '2px solid rgba(255,255,255,0.8)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              🎯 TAP TO ORDER • నొక్కండి →
            </div>
            
            {/* Bottom Highlight */}
            <div style={{
              marginTop: '12px',
              fontSize: '12px',
              color: '#fff59d',
              fontWeight: '600'
            }}>
              ✨ Groceries • Medicine • Food • Documents • Anything! ✨
            </div>
          </div>
        </div>

        {/* Two Features Highlight Section */}
        <div style={{
          margin: '0 15px 15px 15px',
          padding: '15px 20px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '15px',
          boxShadow: '0 6px 25px rgba(102, 126, 234, 0.4)',
          border: '2px solid rgba(255,255,255,0.3)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Sparkle effects */}
          <div style={{ position: 'absolute', top: '5px', left: '10px', fontSize: '16px', opacity: 0.4 }}>✨</div>
          <div style={{ position: 'absolute', bottom: '5px', right: '15px', fontSize: '14px', opacity: 0.4 }}>⭐</div>
          <div style={{ position: 'absolute', top: '50%', right: '40%', fontSize: '12px', opacity: 0.3 }}>💫</div>
          
          {/* Feature 1 - Custom Order */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(255,255,255,0.2)',
            padding: '10px 15px',
            borderRadius: '25px',
            backdropFilter: 'blur(5px)',
            border: '1px solid rgba(255,255,255,0.3)'
          }}>
            <span style={{ fontSize: '22px' }}>📦</span>
            <span style={{ 
              color: 'white', 
              fontWeight: '700', 
              fontSize: '13px',
              textShadow: '1px 1px 3px rgba(0,0,0,0.3)'
            }}>Custom Order</span>
          </div>
          
          {/* Divider */}
          <div style={{
            color: '#fff59d',
            fontWeight: '900',
            fontSize: '20px',
            textShadow: '0 0 10px rgba(255,255,255,0.5)'
          }}>+</div>
          
          {/* Feature 2 - Order Through Stores */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(255,255,255,0.2)',
            padding: '10px 15px',
            borderRadius: '25px',
            backdropFilter: 'blur(5px)',
            border: '1px solid rgba(255,255,255,0.3)'
          }}>
            <span style={{ fontSize: '22px' }}>🏪</span>
            <span style={{ 
              color: 'white', 
              fontWeight: '700', 
              fontSize: '13px',
              textShadow: '1px 1px 3px rgba(0,0,0,0.3)'
            }}>Order from Stores</span>
          </div>
        </div>
        
        {/* Section Title for Stores */}
        <div style={{
          margin: '10px 15px 5px 15px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            padding: '8px 16px',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
          }}>
            <span style={{ fontSize: '18px' }}>🏪</span>
            <span style={{ color: 'white', fontWeight: '700', fontSize: '14px' }}>Browse Stores</span>
            <span style={{ fontSize: '18px' }}>👇</span>
          </div>
          <div style={{ flex: 1, height: '2px', background: 'linear-gradient(90deg, #10b981, transparent)' }}></div>
        </div>

        <LocationSelector onLocationSelect={setSelectedLocation} />
        
        {/* Payment Required Banner - Always visible when payment pending */}
        {paymentPopup && paymentPopup.itemsTotal > 0 && !showOrderStatus && (
          <div style={{
            background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
            color: 'white',
            padding: '20px',
            borderRadius: '15px',
            margin: '15px 0',
            boxShadow: '0 8px 25px rgba(231, 76, 60, 0.5)',
            animation: 'urgentPulse 1.5s infinite',
            border: '3px solid #fff',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>⚠️</div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '22px', fontWeight: 'bold' }}>
                ⚡ PAYMENT REQUIRED
              </h3>
              <p style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '600' }}>
                Your partner is waiting with your items at the store
              </p>
              <p style={{ margin: '0 0 15px 0', fontSize: '24px', fontWeight: 'bold' }}>
                Total: ₹{paymentPopup.totalAmount?.toFixed(2)}
              </p>
              <p style={{ margin: '0', fontSize: '14px', opacity: 0.95 }}>
                ⏰ Please complete payment to get your order delivered
              </p>
            </div>
          </div>
        )}
        
        {/* Store Page View */}
        {selectedStore ? (
          <StorePage 
            store={selectedStore} 
            onBack={handleBackFromStore}
            onAddToCart={handleAddToCart}
            cart={cart}
            onCheckout={handleCheckout}
          />
        ) : (
          <>
            {/* Category Tabs */}
            <CategoryTabs 
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
            />
            
            {/* Store List */}
            <StoreList 
              selectedCategory={selectedCategory}
              onSelectStore={handleSelectStore}
            />
          </>
        )}
        
        {/* Floating Cart Button */}
        {cart.length > 0 && !selectedStore && (
          <div style={{
            position: 'fixed',
            bottom: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            width: '90%',
            maxWidth: '400px'
          }}>
            <button
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '15px',
                padding: '18px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
              onClick={handleCheckout}
            >
              <span>🛒 {cart.reduce((sum, item) => sum + item.quantity, 0)} Items</span>
              <span>Checkout • ₹{cart.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2)}</span>
            </button>
          </div>
        )}

        {/* Checkout Modal */}
        {showCheckout && (
          <CheckoutCart
            cart={cart}
            onClose={() => setShowCheckout(false)}
            onOrderPlaced={handleOrderPlaced}
            selectedLocation={selectedLocation}
            userAddress={savedAddress}
            onRemoveItem={handleRemoveFromCart}
            onUpdateQuantity={handleUpdateCartQuantity}
          />
        )}
      </main>

      {showOrderStatus && (
        <div className="modal-overlay" onClick={() => setShowOrderStatus(false)}>
          <div className="order-status-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Your Orders</h2>
            <div className="order-status-content">
              {loadingOrders ? (
                <p>Loading orders...</p>
              ) : orders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p>No orders yet</p>
                  <p style={{ fontSize: '14px', color: '#666' }}>Place your first order to see it here!</p>
                </div>
              ) : (
                <div className="orders-list">
                  {orders.map((order: any) => (
                    <div key={order._id} className="order-card" style={order.isMultiStoreOrder ? {
                      borderLeft: '4px solid #7c3aed',
                      background: 'linear-gradient(135deg, #f5f3ff, #fff)'
                    } : {}}>
                      {/* Multi-Store Order Banner */}
                      {order.isMultiStoreOrder && (
                        <div style={{
                          background: 'linear-gradient(135deg, #667eea, #764ba2)',
                          color: 'white',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          marginBottom: '12px',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>🏆 Multi-Store Order</div>
                          <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '4px' }}>
                            {order.relatedOrderIds?.length || 2} stores • Single delivery
                          </div>
                        </div>
                      )}
                      <div className="order-header">
                        <span className="order-id">Order #{order._id.slice(-6)}</span>
                        <span className={`order-status status-${order.status.toLowerCase().replace(/ /g, '-')}`}>
                          {order.status}
                        </span>
                      </div>
                      <div className="order-details">
                        <p><strong>{order.isMultiStoreOrder ? 'Stores:' : 'Store:'}</strong> {
                          order.isMultiStoreOrder && order.storeNames 
                            ? order.storeNames.join(' → ') 
                            : (order.store?.name || 'N/A')
                        }</p>
                        <p><strong>Items:</strong> {order.items?.length || 0} items</p>
                        
                        {/* Preparation Time Display */}
                        {order.preparationTime && order.status === 'Confirmed' && (
                          <div style={{
                            background: '#fef3c7',
                            padding: '10px',
                            borderRadius: '8px',
                            marginBottom: '10px',
                            border: '1px solid #fbbf24'
                          }}>
                            <p style={{ margin: 0, color: '#92400e', fontWeight: '600' }}>
                              ⏱️ Preparation Time: {order.preparationTime} minutes
                            </p>
                          </div>
                        )}

                        {/* Custom Order Bill Confirmation */}
                        {order.isCustomOrder && order.customOrderStatus === 'Bill Sent' && order.customOrderBill && (
                          <div style={{
                            background: '#dbeafe',
                            padding: '12px',
                            borderRadius: '8px',
                            marginBottom: '10px',
                            border: '1px solid #3b82f6'
                          }}>
                            <p style={{ margin: '0 0 8px 0', color: '#1e40af', fontWeight: '700' }}>
                              📋 Store has sent your bill
                            </p>
                            <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#1e40af' }}>
                              Bill Amount: ₹{order.customOrderBill.finalAmount}
                            </p>
                            {order.customOrderBill.billNotes && (
                              <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#3b82f6' }}>
                                Note: {order.customOrderBill.billNotes}
                              </p>
                            )}
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => handleConfirmCustomBill(order._id, true)}
                                style={{
                                  flex: 1,
                                  padding: '10px',
                                  background: '#22c55e',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  fontWeight: '600',
                                  cursor: 'pointer'
                                }}
                              >
                                ✅ Confirm & Pay
                              </button>
                              <button
                                onClick={() => handleConfirmCustomBill(order._id, false)}
                                style={{
                                  padding: '10px 15px',
                                  background: '#fee2e2',
                                  color: '#dc2626',
                                  border: 'none',
                                  borderRadius: '8px',
                                  fontWeight: '600',
                                  cursor: 'pointer'
                                }}
                              >
                                ❌ Reject
                              </button>
                            </div>
                          </div>
                        )}

                        {order.itemsTotal ? (
                          <>
                            <p><strong>Items Total:</strong> ₹{order.itemsTotal.toFixed(2)}</p>
                            <p><strong>Delivery Charges:</strong> ₹{order.deliveryCharges?.toFixed(2) || '0.00'}</p>
                            <p style={{fontSize: '18px', color: '#6366f1'}}><strong>Total Amount:</strong> ₹{order.totalAmount?.toFixed(2) || '0.00'}</p>
                            {order.paymentStatus && (
                              <p><strong>Payment:</strong> {order.paymentStatus} ({order.paymentMethod || 'N/A'})</p>
                            )}
                          </>
                        ) : (
                          <p><strong>Estimated Total:</strong> ₹{order.totalAmount?.toFixed(2) || '0.00'}</p>
                        )}
                        <p><strong>Date:</strong> {new Date(order.orderDate).toLocaleDateString()}</p>
                        {order.assignedPartner && (
                          <p><strong>Partner:</strong> {order.assignedPartner.name}</p>
                        )}
                      </div>
                      <div className="order-items">
                        <strong>Items:</strong>
                        <ul>
                          {order.items?.slice(0, 3).map((item: any, idx: number) => (
                            <li key={idx}>{item.name} - {item.quantity}</li>
                          ))}
                          {order.items?.length > 3 && (
                            <li>+{order.items.length - 3} more items</li>
                          )}
                        </ul>
                      </div>
                      {order.status === 'Delivered' ? (
                        <div style={{
                          padding: '14px',
                          background: '#60b246',
                          borderRadius: '8px',
                          textAlign: 'center',
                          color: 'white',
                          fontWeight: '700',
                          marginTop: '8px',
                          fontSize: '16px'
                        }}>
                          ✅ Order Completed
                        </div>
                      ) : order.status === 'Payment Pending' ? (
                        order.itemsTotal > 0 ? (
                          <button 
                            className="payment-button" 
                            onClick={() => {
                              setPaymentPopup(order);
                              setShowOrderStatus(false);
                            }}
                          >
                            💰 Make Payment Now
                          </button>
                        ) : (
                          <div style={{
                            padding: '12px',
                            background: '#fff5ed',
                            borderRadius: '8px',
                            textAlign: 'center',
                            color: '#6366f1',
                            fontWeight: '600',
                            marginTop: '8px',
                            border: '2px solid #6366f1'
                          }}>
                            ⏳ Waiting for partner to send bill...
                          </div>
                        )
                      ) : order.status === 'Pending' || order.status === 'Confirmed' ? (
                        <div style={{display: 'flex', gap: '10px', marginTop: '8px'}}>
                          <button 
                            className="track-button" 
                            onClick={() => handleTrackOrder(order._id)}
                            style={{flex: 1}}
                          >
                            📍 Track Order
                          </button>
                          {!order.assignedPartner && order.status === 'Pending' && (
                            <button 
                              className="cancel-order-button" 
                              onClick={() => handleCancelOrder(order._id)}
                              style={{flex: 1}}
                            >
                              ✕ Cancel Order
                            </button>
                          )}
                          {order.assignedPartner && (
                            <button 
                              className="chat-button" 
                              onClick={() => {
                                setChatModal({
                                  orderId: order._id,
                                  partnerName: order.assignedPartner?.name || 'Delivery Partner',
                                  orderStatus: order.status
                                });
                                setShowOrderStatus(false);
                              }}
                              style={{flex: 1}}
                            >
                              💬 Chat
                            </button>
                          )}
                        </div>
                      ) : (
                        <div style={{display: 'flex', gap: '10px', marginTop: '8px'}}>
                          <button 
                            className="track-button" 
                            onClick={() => handleTrackOrder(order._id)}
                            style={{flex: 1}}
                          >
                            📍 Track Order
                          </button>
                          {order.assignedPartner && (
                            <button 
                              className="chat-button" 
                              onClick={() => {
                                setChatModal({
                                  orderId: order._id,
                                  partnerName: order.assignedPartner?.name || 'Delivery Partner',
                                  orderStatus: order.status
                                });
                                setShowOrderStatus(false);
                              }}
                              style={{flex: 1}}
                            >
                              💬 Chat
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button className="close-profile-button" onClick={() => setShowOrderStatus(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {showProfile && (
        <div className="modal-overlay" onClick={() => setShowProfile(false)}>
          <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Your Profile</h2>
            <div className="profile-content">
              <div className="profile-item">
                <div className="profile-info">
                  <label>Name</label>
                  <p>{userName}</p>
                </div>
                <button className="edit-button" onClick={handleChangeName}>
                  ✏️ Change Name
                </button>
              </div>
              
              <div className="profile-item">
                <div className="profile-info">
                  <label>Saved Address</label>
                  <p>{savedAddress}</p>
                </div>
                <button className="edit-button" onClick={handleChangeAddress}>
                  ✏️ Change Address
                </button>
              </div>
            </div>
            <button 
              className="logout-button" 
              onClick={handleLogout}
              style={{
                background: '#d32f2f',
                color: 'white',
                padding: '12px',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginTop: '15px',
                width: '100%'
              }}
            >
              🚪 Logout
            </button>
            <button className="close-profile-button" onClick={() => setShowProfile(false)}>
              Close
            </button>
          </div>
        </div>
      )}

      {showWelcomePopup && (
        <div className="modal-overlay" style={{zIndex: 10001}}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={{color: '#6366f1', marginBottom: '20px'}}>⚠️ Important Notice</h2>
            <div className="payment-content">
              <div style={{
                background: 'linear-gradient(135deg, #fff5ed 0%, #ffe8d6 100%)',
                padding: '25px',
                borderRadius: '12px',
                border: '2px solid #6366f1',
                textAlign: 'center'
              }}>
                <p style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#282c3f',
                  lineHeight: '1.6',
                  margin: 0
                }}>
                  🚴 <strong>We are delivery partners only.</strong>
                </p>
                <p style={{
                  fontSize: '16px',
                  color: '#686b78',
                  lineHeight: '1.6',
                  marginTop: '15px'
                }}>
                  We pick up and deliver items for you — we do not provide or sell any products.
                </p>
              </div>

              <button 
                className="confirm-payment-button" 
                onClick={() => setShowWelcomePopup(false)}
                style={{marginTop: '25px'}}
              >
                ✅ I Understand
              </button>
            </div>
          </div>
        </div>
      )}

      {partnerArrivedPopup && (
        <div className="modal-overlay" style={{zIndex: 10000}}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🚴 Partner Arrived!</h2>
            <div className="payment-content">
              <div className="partner-message">
                <p style={{fontSize: '20px', fontWeight: 'bold', color: '#282c3f', marginBottom: '10px'}}>
                  🎉 Your delivery partner has reached your location!
                </p>
                <p style={{fontSize: '16px', color: '#686b78'}}>
                  Please collect your order and confirm delivery by entering the OTP shown by the partner.
                </p>
              </div>

              <div style={{
                background: '#fff5ed',
                padding: '20px',
                borderRadius: '12px',
                border: '2px solid #6366f1',
                marginTop: '20px'
              }}>
                <label style={{display: 'block', marginBottom: '12px', fontWeight: '600', color: '#282c3f', textAlign: 'center'}}>
                  💰 Add Tip for Partner (Optional)
                </label>
                <p style={{fontSize: '13px', color: '#7e808c', marginBottom: '16px', textAlign: 'center'}}>
                  Show your appreciation to the delivery partner
                </p>
                <div style={{display: 'flex', gap: '10px', marginBottom: '16px'}}>
                  <button
                    onClick={() => handleDeliveryTipSelect(10)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: deliveryTip === 10 ? '#6366f1' : 'white',
                      color: deliveryTip === 10 ? 'white' : '#282c3f',
                      border: '2px solid ' + (deliveryTip === 10 ? '#6366f1' : '#e7e9ed'),
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    ₹10
                  </button>
                  <button
                    onClick={() => handleDeliveryTipSelect(20)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: deliveryTip === 20 ? '#6366f1' : 'white',
                      color: deliveryTip === 20 ? 'white' : '#282c3f',
                      border: '2px solid ' + (deliveryTip === 20 ? '#6366f1' : '#e7e9ed'),
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    ₹20
                  </button>
                </div>
                <div style={{marginBottom: '16px'}}>
                  <input
                    type="number"
                    value={customDeliveryTip}
                    onChange={(e) => setCustomDeliveryTip(e.target.value)}
                    onBlur={handleCustomDeliveryTip}
                    placeholder="Or enter custom amount"
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '14px',
                      border: '2px solid #e7e9ed',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                {deliveryTip > 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: '10px',
                    backgroundColor: '#60b246',
                    color: 'white',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    marginBottom: '16px'
                  }}>
                    Selected Tip: ₹{deliveryTip}
                  </div>
                )}
                <button
                  onClick={() => { setDeliveryTip(0); setCustomDeliveryTip(''); }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: 'white',
                    color: '#7e808c',
                    border: '1px solid #e7e9ed',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Skip Tip
                </button>
              </div>

              <div style={{
                background: '#f9f9f9',
                padding: '20px',
                borderRadius: '12px',
                border: '2px solid #60b246',
                marginTop: '20px'
              }}>
                <label style={{display: 'block', marginBottom: '10px', fontWeight: '600', color: '#282c3f'}}>
                  Enter OTP to Confirm Delivery:
                </label>
                <input
                  type="text"
                  value={otpInput}
                  onChange={(e) => setOtpInput(e.target.value)}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                  style={{
                    width: '100%',
                    padding: '15px',
                    fontSize: '24px',
                    textAlign: 'center',
                    border: '2px solid #d4d5d9',
                    borderRadius: '10px',
                    letterSpacing: '8px',
                    fontWeight: 'bold',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <button 
                className="confirm-payment-button" 
                onClick={handleConfirmDelivery}
                style={{marginTop: '20px'}}
              >
                ✅ Confirm Delivery
              </button>
              <button 
                className="cancel-payment-button" 
                onClick={() => setPartnerArrivedPopup(null)}
              >
                Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Direct Custom Order Payment Popup - Partner Accepted */}
      {directOrderPaymentPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            maxWidth: '400px',
            width: '100%',
            padding: '25px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '60px',
              marginBottom: '15px'
            }}>🎉</div>
            
            <h2 style={{
              color: '#059669',
              margin: '0 0 10px',
              fontSize: '22px'
            }}>Partner Accepted!</h2>
            
            <p style={{
              color: '#6b7280',
              margin: '0 0 20px',
              fontSize: '14px'
            }}>
              మీ ఆర్డర్ పార్ట్నర్ అంగీకరించారు<br/>
              Your order has been accepted by a delivery partner
            </p>

            <div style={{
              background: '#f0fdf4',
              padding: '15px',
              borderRadius: '12px',
              marginBottom: '20px'
            }}>
              <p style={{ margin: '0 0 8px', fontSize: '14px', color: '#166534' }}>
                <strong>🛵 Partner:</strong> {directOrderPaymentPopup.assignedPartner?.name || 'Partner'}
              </p>
              <p style={{ margin: '0', fontSize: '14px', color: '#166534' }}>
                <strong>📞 Phone:</strong> {directOrderPaymentPopup.assignedPartner?.phone || 'N/A'}
              </p>
            </div>

            <div style={{
              background: '#fef3c7',
              padding: '15px',
              borderRadius: '12px',
              marginBottom: '20px'
            }}>
              <p style={{ margin: '0 0 5px', fontSize: '13px', color: '#92400e' }}>
                <strong>💰 Delivery Charge:</strong> ₹{directOrderPaymentPopup.deliveryCharges || 60}
              </p>
              <p style={{ margin: '0', fontSize: '12px', color: '#b45309' }}>
                + Item costs (pay after partner buys items)
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
              <a
                href={`tel:${directOrderPaymentPopup.assignedPartner?.phone}`}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#dcfce7',
                  color: '#166534',
                  textDecoration: 'none',
                  borderRadius: '10px',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                📞 Call Partner
              </a>
              <a
                href={`https://wa.me/91${(directOrderPaymentPopup.assignedPartner?.phone || '').replace(/\D/g, '').slice(-10)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#d1fae5',
                  color: '#065f46',
                  textDecoration: 'none',
                  borderRadius: '10px',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                💬 WhatsApp
              </a>
            </div>

            <a
              href={`upi://pay?pa=${directOrderPaymentPopup.assignedPartner?.upiId || 'partner@upi'}&pn=${encodeURIComponent(directOrderPaymentPopup.assignedPartner?.name || 'Partner')}&am=${directOrderPaymentPopup.deliveryCharges || 60}&cu=INR&tn=${encodeURIComponent('BringIt Delivery Charge - Order #' + directOrderPaymentPopup._id?.slice(-6))}`}
              style={{
                display: 'block',
                width: '100%',
                padding: '15px',
                background: 'linear-gradient(135deg, #7c3aed, #ec4899)',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '12px',
                fontWeight: 'bold',
                fontSize: '16px',
                marginBottom: '10px',
                boxSizing: 'border-box'
              }}
            >
              💳 Pay ₹{directOrderPaymentPopup.deliveryCharges || 60} via UPI
            </a>

            <button
              onClick={() => setDirectOrderPaymentPopup(null)}
              style={{
                width: '100%',
                padding: '12px',
                background: '#f3f4f6',
                color: '#6b7280',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Store Payment Popup - Partner sent QR code for customer to pay at store */}
      {storePaymentPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            maxWidth: '400px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '25px'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '50px', marginBottom: '10px' }}>💳</div>
              <h2 style={{ margin: '0 0 5px', color: '#7c3aed', fontSize: '20px' }}>Pay at Store</h2>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '13px' }}>
                స్టోర్‌కి చెల్లించండి / Complete payment to store
              </p>
            </div>

            {/* Store Info */}
            <div style={{
              background: '#f0fdf4',
              padding: '12px',
              borderRadius: '10px',
              marginBottom: '15px'
            }}>
              <p style={{ margin: '0 0 5px', fontSize: '14px', color: '#166534' }}>
                <strong>🏪 Store:</strong> {storePaymentPopup.directPaymentRequest?.storeName || 'Store'}
              </p>
              {storePaymentPopup.directPaymentRequest?.storePhone && (
                <p style={{ margin: 0, fontSize: '14px', color: '#166534' }}>
                  <strong>📞 Phone:</strong> {storePaymentPopup.directPaymentRequest?.storePhone}
                </p>
              )}
            </div>

            {/* Amount */}
            <div style={{
              background: 'linear-gradient(135deg, #7c3aed, #ec4899)',
              padding: '15px',
              borderRadius: '12px',
              marginBottom: '15px',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, color: 'white', fontSize: '14px' }}>Amount to Pay</p>
              <p style={{ margin: '5px 0 0', color: 'white', fontSize: '28px', fontWeight: 'bold' }}>
                ₹{storePaymentPopup.directPaymentRequest?.amount || 0}
              </p>
            </div>

            {/* QR Code Image */}
            {storePaymentPopup.directPaymentRequest?.qrCodeImage && (
              <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#6b7280' }}>
                  Scan QR Code to Pay / QR కోడ్ స్కాన్ చేయండి
                </p>
                <img 
                  src={storePaymentPopup.directPaymentRequest.qrCodeImage} 
                  alt="Store QR Code"
                  style={{
                    maxWidth: '250px',
                    maxHeight: '250px',
                    borderRadius: '12px',
                    border: '2px solid #e2e8f0'
                  }}
                />
              </div>
            )}

            {/* Call Store */}
            {storePaymentPopup.directPaymentRequest?.storePhone && (
              <a
                href={`tel:${storePaymentPopup.directPaymentRequest.storePhone}`}
                style={{
                  display: 'block',
                  padding: '12px',
                  background: '#dcfce7',
                  color: '#166534',
                  textDecoration: 'none',
                  borderRadius: '10px',
                  textAlign: 'center',
                  fontWeight: '600',
                  marginBottom: '15px'
                }}
              >
                📞 Call Store
              </a>
            )}

            {/* Upload Payment Receipt */}
            <div style={{
              background: '#fef3c7',
              padding: '15px',
              borderRadius: '12px',
              marginBottom: '15px'
            }}>
              <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#92400e', fontWeight: '600' }}>
                📷 After payment, send receipt to partner
              </p>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setPaymentReceiptImage(reader.result as string);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px dashed #f59e0b',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                }}
              />
              {paymentReceiptImage && (
                <div style={{ marginTop: '10px', textAlign: 'center' }}>
                  <img 
                    src={paymentReceiptImage} 
                    alt="Receipt" 
                    style={{ maxWidth: '150px', borderRadius: '8px' }} 
                  />
                </div>
              )}
            </div>

            <button
              onClick={async () => {
                if (!paymentReceiptImage) {
                  alert('Please take a photo of your payment receipt');
                  return;
                }
                setSendingReceipt(true);
                try {
                  const token = localStorage.getItem('token');
                  await fetch(`https://api.bringit.co.in/api/orders/${storePaymentPopup._id}/payment-receipt`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ receiptImage: paymentReceiptImage })
                  });
                  alert('✅ Receipt sent to partner! They will confirm and start shopping.');
                  setStorePaymentPopup(null);
                  setPaymentReceiptImage('');
                } catch (error) {
                  alert('Failed to send receipt. Please try again.');
                } finally {
                  setSendingReceipt(false);
                }
              }}
              disabled={sendingReceipt || !paymentReceiptImage}
              style={{
                width: '100%',
                padding: '15px',
                background: sendingReceipt || !paymentReceiptImage ? '#9ca3af' : '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontWeight: 'bold',
                fontSize: '15px',
                cursor: sendingReceipt || !paymentReceiptImage ? 'not-allowed' : 'pointer',
                marginBottom: '10px'
              }}
            >
              {sendingReceipt ? '⏳ Sending...' : '📤 Send Receipt to Partner'}
            </button>

            <button
              onClick={() => setStorePaymentPopup(null)}
              style={{
                width: '100%',
                padding: '12px',
                background: '#f3f4f6',
                color: '#6b7280',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {paymentPopup && (
        <PaymentScreen 
          order={paymentPopup} 
          onPaymentComplete={handlePaymentComplete}
        />
      )}

      <footer className="app-footer">
        <button className="footer-button" onClick={handleOrderStatus}>
          📦 Order Status
        </button>
        <button className="footer-button" onClick={handleYourOrders}>
          🛒 Your Orders
        </button>
        <button className="footer-button" onClick={handleYourProfile}>
          👤 Your Profile
        </button>
      </footer>

      {/* Chat Modal */}
      {chatModal && (
        <CustomerChatModal
          partnerName={chatModal.partnerName}
          orderId={chatModal.orderId}
          orderStatus={chatModal.orderStatus}
          onClose={() => setChatModal(null)}
        />
      )}

      {/* Direct Custom Order Modal (Order Anything) */}
      {showDirectCustomOrder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#7c3aed' }}>🛒 Order Anything</h2>
              <button
                onClick={() => {
                  setShowDirectCustomOrder(false);
                  setDirectOrderDescription('');
                  setDirectOrderItems([{ name: '', quantity: '' }]);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #f0e6ff, #fff0f5)',
              padding: '15px',
              borderRadius: '12px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b21a8', fontWeight: '600' }}>
                మీకు కావలసిన వస్తువులను నమోదు చేయండి
              </p>
              <p style={{ margin: '5px 0 0', fontSize: '13px', color: '#7c3aed' }}>
                Enter the items you need - we'll deliver anything!
              </p>
              <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#8b5cf6' }}>
                💰 Delivery Charge: ₹60 (Prepaid Only)
              </p>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontWeight: '600', color: '#333', display: 'block', marginBottom: '8px' }}>
                What do you need? / మీకు ఏమి కావాలి?
              </label>
              <textarea
                value={directOrderDescription}
                onChange={(e) => setDirectOrderDescription(e.target.value)}
                placeholder="Describe what you want... (e.g., Groceries from any store, medicine from pharmacy, food from a restaurant)"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '2px solid #e2e8f0',
                  fontSize: '14px',
                  minHeight: '80px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontWeight: '600', color: '#333', display: 'block', marginBottom: '8px' }}>
                Items List / వస్తువుల జాబితా
              </label>
              {directOrderItems.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <input
                    type="text"
                    placeholder="Item name / వస్తువు పేరు"
                    value={item.name}
                    onChange={(e) => {
                      const newItems = [...directOrderItems];
                      newItems[idx].name = e.target.value;
                      setDirectOrderItems(newItems);
                    }}
                    style={{
                      flex: 2,
                      padding: '10px',
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      fontSize: '14px'
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Qty / పరిమాణం"
                    value={item.quantity}
                    onChange={(e) => {
                      const newItems = [...directOrderItems];
                      newItems[idx].quantity = e.target.value;
                      setDirectOrderItems(newItems);
                    }}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      fontSize: '14px'
                    }}
                  />
                  {directOrderItems.length > 1 && (
                    <button
                      onClick={() => {
                        const newItems = directOrderItems.filter((_, i) => i !== idx);
                        setDirectOrderItems(newItems);
                      }}
                      style={{
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setDirectOrderItems([...directOrderItems, { name: '', quantity: '' }])}
                style={{
                  background: '#f3e8ff',
                  color: '#7c3aed',
                  border: '2px dashed #7c3aed',
                  borderRadius: '8px',
                  padding: '10px 15px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  width: '100%'
                }}
              >
                + Add More Items / మరిన్ని జోడించండి
              </button>
            </div>

            <div style={{
              background: '#fef3c7',
              padding: '12px',
              borderRadius: '10px',
              marginBottom: '20px'
            }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#92400e' }}>
                ⚠️ <strong>Prepaid Only:</strong> You'll pay the partner after they accept your order. Delivery charge ₹60 + Item costs.
              </p>
            </div>

            <button
              onClick={async () => {
                if (!directOrderDescription.trim() && !directOrderItems.some(i => i.name.trim())) {
                  alert('Please describe what you need or add items');
                  return;
                }

                const token = localStorage.getItem('token');
                if (!token) {
                  alert('Please login to place order');
                  return;
                }

                setDirectOrderSubmitting(true);
                try {
                  const payload = {
                    description: directOrderDescription,
                    items: directOrderItems.filter(i => i.name.trim()).map(i => ({
                      name: i.name.trim(),
                      quantity: i.quantity.trim() || '1'
                    })),
                    deliveryAddress: userLocation
                  };

                  const response = await fetch('https://api.bringit.co.in/api/orders/direct-custom', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(payload)
                  });

                  const data = await response.json();
                  
                  if (response.ok) {
                    alert('✅ Order placed! A delivery partner will accept your order soon. You will pay the partner directly.');
                    setShowDirectCustomOrder(false);
                    setDirectOrderDescription('');
                    setDirectOrderItems([{ name: '', quantity: '' }]);
                    fetchOrders(); // Refresh orders
                  } else {
                    alert(data.message || 'Failed to place order');
                  }
                } catch (error) {
                  console.error('Error placing direct order:', error);
                  alert('Failed to place order. Please try again.');
                } finally {
                  setDirectOrderSubmitting(false);
                }
              }}
              disabled={directOrderSubmitting}
              style={{
                width: '100%',
                padding: '15px',
                background: directOrderSubmitting ? '#9ca3af' : 'linear-gradient(135deg, #7c3aed, #ec4899)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: directOrderSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              {directOrderSubmitting ? '⏳ Placing Order...' : '🚀 Place Order / ఆర్డర్ చేయండి'}
            </button>
          </div>
        </div>
      )}

      {/* Order Tracking Map Modal */}
      {trackingOrder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '25px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => setTrackingOrder(null)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '35px',
                height: '35px',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>

            <h2 style={{ marginBottom: '20px', color: '#6366f1' }}>
              📍 Track Order #{trackingOrder._id.slice(-6)}
            </h2>

            <OrderTrackingMap
              orderId={trackingOrder._id}
              customerLocation={
                trackingOrder.deliveryAddress?.coordinates || 
                selectedLocation?.coordinates || 
                { lat: 17.3850, lng: 78.4867 } // Default: Hyderabad
              }
              partnerLocation={trackingOrder.partnerLocation}
              storeLocation={trackingOrder.storeLocation}
              orderStatus={trackingOrder.status}
            />

            <div style={{
              marginTop: '20px',
              padding: '15px',
              background: '#f8f9fa',
              borderRadius: '10px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span><strong>Order ID:</strong></span>
                <span>#{trackingOrder._id.slice(-6)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span><strong>Status:</strong></span>
                <span style={{ color: '#6366f1', fontWeight: 'bold' }}>{trackingOrder.status}</span>
              </div>
              
              {/* Preparation Time */}
              {trackingOrder.preparationTime && (trackingOrder.status === 'Confirmed' || trackingOrder.status === 'Preparing') && (
                <div style={{ 
                  background: '#fef3c7', 
                  padding: '10px', 
                  borderRadius: '8px', 
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '20px' }}>⏱️</span>
                  <span style={{ color: '#92400e', fontWeight: '600' }}>
                    Prep Time: {trackingOrder.preparationTime} min
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span><strong>Items:</strong></span>
                <span>{trackingOrder.items?.length || 0} items</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                <span><strong>Total:</strong></span>
                <span style={{ fontWeight: 'bold' }}>₹{trackingOrder.totalAmount || trackingOrder.itemsTotal || 0}</span>
              </div>

              {/* Store Contact */}
              {trackingOrder.storeId?.phone && (
                <div style={{ marginBottom: '10px' }}>
                  <strong>🏪 Store Contact:</strong>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '5px' }}>
                    <a 
                      href={`tel:${trackingOrder.storeId.phone}`}
                      style={{
                        flex: 1,
                        padding: '8px',
                        background: '#dcfce7',
                        color: '#166534',
                        textDecoration: 'none',
                        borderRadius: '8px',
                        textAlign: 'center',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      📞 Call Store
                    </a>
                  </div>
                </div>
              )}

              {/* Partner Contact */}
              {trackingOrder.assignedPartner?.phone && (
                <div>
                  <strong>🛵 Partner: {trackingOrder.assignedPartner.name}</strong>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '5px' }}>
                    <a 
                      href={`tel:${trackingOrder.assignedPartner.phone}`}
                      style={{
                        flex: 1,
                        padding: '8px',
                        background: '#dcfce7',
                        color: '#166534',
                        textDecoration: 'none',
                        borderRadius: '8px',
                        textAlign: 'center',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      📞 Call Partner
                    </a>
                    <a 
                      href={`https://wa.me/91${trackingOrder.assignedPartner.phone.replace(/\D/g, '').slice(-10)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        flex: 1,
                        padding: '8px',
                        background: '#d1fae5',
                        color: '#065f46',
                        textDecoration: 'none',
                        borderRadius: '8px',
                        textAlign: 'center',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      💬 WhatsApp
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
